'use client';

import { useFiltersContext } from '@components/FilterPage/Context/useFiltersContext';
import { Flex, freddyTheme, Grid, Link, List, Typography } from '@coolblue-development/freddy';
import clearAllFiltersTrigger from '@coolblue-development/ga4-events/triggers/RecruitmentFilter/clearAllFiltersTrigger';
import { CheckmarkCircled } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useTranslation } from '@i18n/useTranslation';
import { FilterCategories, FilterOptions, Filters } from '@server/careers-backend/search';

import { SelectedRow } from './SelectedRow';

type Props = {
  filters: Filters | undefined;
};

export const SelectedFilters = ({ filters }: Props) => {
  const { t } = useTranslation('filterPage.filterFacets');
  const { getSelectedFilterOptions, clearFilters } = useFiltersContext();
  const { trackEvent } = useAnalytics();

  const translatedAnd = t('selectedFilters.andWord');

  const formatSelectedValues = (
    filterItems: FilterOptions,
    values: string[],
    filterKey: FilterCategories
  ) => {
    const formattedValues: string[] = [];

    if (filterKey === 'hours' && 'min' in filterItems && 'max' in filterItems) {
      formattedValues.push(t('selectedFilters.selectedHours', { count: Number(values[0]) }));
    } else if (Array.isArray(filterItems)) {
      formattedValues.push(
        ...values.map(value => {
          return filterItems.find(item => item?.filterKey === value)?.displayName || value;
        })
      );
    }

    if (formattedValues.length === 0) return '';
    if (formattedValues.length === 1) return formattedValues[0];

    const lastValue = formattedValues.at(-1);
    const restValues = formattedValues.slice(0, -1);
    return `${restValues.join(', ')} ${translatedAnd} ${lastValue}`;
  };

  return (
    <Grid templateColumns='1fr' gap={200}>
      <Flex justifyContent='space-between' wrap='wrap' alignItems='center'>
        <Flex alignItems='center'>
          <CheckmarkCircled color={freddyTheme.color.icon.sentimentPositive} />
          <Typography variant='h3' mx={freddyTheme.spacing[200]} mb={0}>
            {t('selectedFilters.title')}
          </Typography>
        </Flex>
        <Link
          component='button'
          onClick={() => {
            clearFilters();
            trackEvent(clearAllFiltersTrigger({ sub_feature: 'filter_list' }));
          }}
        >
          {t('selectedFilters.clearAll')}
        </Link>
      </Flex>

      <List marker='none'>
        {Object.entries(filters || {}).map(([key, filterItems]) => {
          const filterKey = key as FilterCategories;
          const values = getSelectedFilterOptions(filterKey);

          if (values.length === 0) {
            return null;
          }

          return (
            <SelectedRow
              key={filterKey}
              filterKey={filterKey}
              label={t(`${filterKey}.label`)}
              filterValues={formatSelectedValues(filterItems, values, filterKey)}
            />
          );
        })}
      </List>
    </Grid>
  );
};
